import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { ThreeDots } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { getApi, postApi } from "../../utils/api";
import {
  API_LEAVE_GET_OOD_DL_LIST,
  API_GET_ACCESS_TYPES,
  API_ADD_ACCESS_REQUEST,
  API_GET_ALL_ACTIVE_PROJECTS,
} from "../../config/Endpoints";
import { formatToDateOnly } from "../../utils/helpers";
import {
  Button,
  DatePicker,
  DatePickerFieldTrigger,
  Select,
  TextArea,
} from "../../v2/components/ui/index.ts";
import { showError, showSuccess } from "../../v2/utils/toast.ts";

import styles from "./it-requests-form.module.css";

const ALL_PROJECTS_SENTINEL = "__ALL__";
const END_OPTION_VALUES = [
  { value: "ONGOING", label: "Ongoing" },
  { value: "DATE", label: "Select Date" },
];

const AccessRequest = () => {
  const accessToken = Cookies.get("accessToken");
  const [deliveryLead, setDeliveryLead] = useState([]);
  const [endOption, setEndOption] = useState(""); // "ONGOING" or "DATE"
  const [endDate, setEndDate] = useState(null);
  const [accessTypes, setAccessTypes] = useState([]);
  const [isLoading, setIsLoading] = useState();
  const [projects, setProjects] = useState([]);
  const [showTypeField, setShowTypeField] = useState(false);
  const [errors, setErrors] = useState({});
  const [accessSubtypes, setAccessSubtypes] = useState([]);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    projectId: null,
    accessType: "",
    deliveryLead: "",
    reason: "",
    type: "",
  });
  const userData = localStorage.getItem("userData");
  const employee = userData ? JSON.parse(userData) : null;
  const employeeId = employee?.id;

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prevFormData) => ({
      ...prevFormData,
      [name]: "",
    }));
  };

  const handleReset = () => {
    setFormData({
      projectId: null,
      accessType: "",
      deliveryLead: "",
      reason: "",
      type: "",
    });
    setEndOption("");
    setEndDate(null);
    setShowTypeField(false);
    setErrors({});
  };

  const validationSchema = Yup.object().shape({
    reason: Yup.string()
      .required("Reason is required.")
      .trim()
      .min(5, "Reason must be at least 5 characters long.")
      .max(200, "Reason cannot exceed 200 characters."),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // if (
    //   !formData.projectId ||
    //   !formData.accessType ||
    //   !formData.deliveryLead ||
    //   (showTypeField && !formData.type)
    // ) {
    //   return;
    // }

    setIsLoading(true);

    try {
      await validationSchema.validate(formData, { abortEarly: false });

      const payload = {
        projectId: formData.projectId,
        accessId: Number(formData.accessType),
        reason: formData.reason,
        firstApprover: Number(formData.deliveryLead),
        ...(showTypeField && { type: formData.type }),
        endDate:
          endOption === "ONGOING" || !endDate
            ? null
            : formatToDateOnly(endDate),
      };

      const response = await postApi(
        `${API_ADD_ACCESS_REQUEST}/${employeeId}`,
        payload,
        accessToken,
      );

      const statusCode = response?.statusCode || response?.data?.statusCode;

      if (statusCode === 200) {
        showSuccess(
          response?.data?.message ??
            response?.message ??
            "Access request submitted successfully!",
        );

        setFormData({
          projectId: null,
          accessType: "",
          deliveryLead: "",
          reason: "",
          type: "",
        });
        navigate("/dashboard/assetRequest?tab=access");

        return;
      }

      showError(
        response?.data?.message ??
          response?.message ??
          "Failed to submit access request",
      );
    } catch (error) {
      if (error.inner) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        showError(
          error?.response?.data?.message ?? "Something went wrong!",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchDeliveryLeads = async () => {
      try {
        const response = await getApi(API_LEAVE_GET_OOD_DL_LIST, accessToken);

        const leads = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        setDeliveryLead([
          ...leads.map((lead) => ({
            value: lead.id,
            label: lead.fullName || "",
          })),
        ]);
      } catch (error) {
        setDeliveryLead([]);
      }
    };

    fetchDeliveryLeads();
  }, []);

  useEffect(() => {
    const fetchAccessTypes = async () => {
      try {
        const res = await getApi(
          `${API_GET_ACCESS_TYPES}?status=APPROVED`,
          accessToken,
        );
        const accessTypesData = res?.data?.data?.accessTypes || [];
        const accessSubtypesData = res?.data?.data?.accessSubtypes || [];

        const types = accessTypesData.map((at) => ({
          value: at.accessId,
          label: at.name,
          isRole: at.isRole,
        }));

        setAccessTypes(types);
        setAccessSubtypes(accessSubtypesData);
      } catch (error) {
        setAccessTypes([]);
        setAccessSubtypes([]);
      }
    };
    fetchAccessTypes();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await getApi(API_GET_ALL_ACTIVE_PROJECTS, accessToken);
        const projectsData = res?.data?.data || res?.data || [];

        if (Array.isArray(projectsData)) {
          const formatted = projectsData.map((p) => ({
            value: p.projectId,
            label: p.projectName,
          }));

          setProjects([{ value: "", label: "All" }, ...formatted]);
        }
      } catch (error) {
        setProjects([{ value: "", label: "All" }]);
      }
    };

    fetchProjects();
  }, []);

  // ----- v2 Select adapters -----
  // The v2 Select operates exclusively on string values; the surrounding logic
  // relies on the original API value types (numbers, "" for "All", null for
  // "no selection"). The adapters below translate between those representations
  // without altering the underlying form state shape.

  const accessTypeOptions = useMemo(
    () =>
      accessTypes.map((item) => ({
        value: String(item.value),
        label: item.label,
      })),
    [accessTypes],
  );

  const accessTypeValue =
    formData.accessType !== "" && formData.accessType != null
      ? String(formData.accessType)
      : "";

  const handleAccessTypeChange = (nextValue) => {
    if (nextValue === "") {
      setShowTypeField(false);
      setFormData((prev) => ({
        ...prev,
        accessType: "",
        type: "",
      }));
      return;
    }

    const matched = accessTypes.find(
      (item) => String(item.value) === nextValue,
    );
    const accessId = matched ? matched.value : nextValue;

    const show = matched?.isRole === 1;
    setShowTypeField(show);

    if (accessId === 18) {
      setFormData((prev) => ({
        ...prev,
        accessType: accessId,
        type: "",
        deliveryLead: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        accessType: accessId,
        type: "",
      }));
    }
  };

  const roleOptions = useMemo(() => {
    if (formData.accessType === 18) {
      return accessSubtypes
        .filter((s) => s === "Admin" || s === "Developer")
        .map((s) => ({ label: s, value: s }));
    }
    return accessSubtypes
      .filter((s) => s === "User" || s === "Admin")
      .map((s) => ({ label: s, value: s }));
  }, [accessSubtypes, formData.accessType]);

  const projectOptions = useMemo(() => {
    return projects.map((p) =>
      p.value === ""
        ? { value: ALL_PROJECTS_SENTINEL, label: p.label }
        : { value: String(p.value), label: p.label },
    );
  }, [projects]);

  const projectValue =
    formData.projectId == null
      ? ""
      : formData.projectId === ""
        ? ALL_PROJECTS_SENTINEL
        : String(formData.projectId);

  const handleProjectChange = (nextValue) => {
    if (nextValue === "") {
      setFormData((prev) => ({ ...prev, projectId: null }));
      return;
    }
    if (nextValue === ALL_PROJECTS_SENTINEL) {
      setFormData((prev) => ({ ...prev, projectId: "" }));
      return;
    }
    const matched = projects.find(
      (p) => p.value !== "" && String(p.value) === nextValue,
    );
    setFormData((prev) => ({
      ...prev,
      projectId: matched ? matched.value : nextValue,
    }));
  };

  const deliveryLeadOptions = useMemo(
    () =>
      deliveryLead.map((dl) => ({
        value: String(dl.value),
        label: dl.label,
      })),
    [deliveryLead],
  );

  const deliveryLeadValue =
    formData.deliveryLead !== "" && formData.deliveryLead != null
      ? String(formData.deliveryLead)
      : "";

  const handleDeliveryLeadChange = (nextValue) => {
    if (nextValue === "") {
      setFormData((prev) => ({ ...prev, deliveryLead: "" }));
      return;
    }
    const matched = deliveryLead.find(
      (dl) => String(dl.value) === nextValue,
    );
    setFormData((prev) => ({
      ...prev,
      deliveryLead: matched ? matched.value : nextValue,
    }));
  };

  const handleEndOptionChange = (nextValue) => {
    setEndOption(nextValue || "");
    if (nextValue === "ONGOING") setEndDate(null);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  return (
    <form
      id="access-request"
      onSubmit={handleSubmit}
      className={styles.formRoot}
    >
      <div className="inputs">
        <div className="row">
          <div className="col-md-2"></div>
          <div className="col-md-7">
            <Select
              id="accessType"
              label="Access Type"
              labelLayout="inline"
              required
              placeholder="Select Access Type"
              options={accessTypeOptions}
              value={accessTypeValue}
              onChange={handleAccessTypeChange}
              isClearable
              searchable
            />
          </div>
          <div className="col-md-3"></div>
        </div>

        {showTypeField ? (
          <div className="row mt-1">
            <div className="col-md-2"></div>
            <div className="col-md-7">
              <Select
                id="type"
                label="Role"
                labelLayout="inline"
                required
                placeholder="Select Role"
                options={roleOptions}
                value={formData.type ? formData.type : ""}
                onChange={(v) =>
                  setFormData((prev) => ({ ...prev, type: v }))
                }
                isClearable
                searchable
              />
            </div>
            <div className="col-md-3"></div>
          </div>
        ) : null}

        <div className="row mt-1">
          <div className="col-md-2"></div>
          <div className="col-md-7">
            <Select
              id="projectId"
              label="Project"
              labelLayout="inline"
              required
              placeholder="Select Project"
              options={projectOptions}
              value={projectValue}
              onChange={handleProjectChange}
              isClearable
              searchable
            />
          </div>
          <div className="col-md-3"></div>
        </div>

        <div className="row mt-1">
          <div className="col-md-2"></div>
          <div className="col-md-7">
            <Select
              id="deliveryLead"
              label="Delivery Lead"
              labelLayout="inline"
              required
              placeholder={
                formData.accessType === 18 ? "Management" : "Select Delivery Lead"
              }
              options={deliveryLeadOptions}
              value={deliveryLeadValue}
              onChange={handleDeliveryLeadChange}
              disabled={formData.accessType === 18}
              isClearable
              searchable
            />
          </div>
          <div className="col-md-3"></div>
        </div>

        <div className="row mt-1">
          <div className="col-md-2"></div>
          <div className="col-md-7">
            {endOption === "DATE" ? (
              <DatePicker
                id="accessEndDate"
                label="End Date"
                labelLayout="inline"
                selected={endDate ? new Date(endDate) : null}
                onChange={handleEndDateChange}
                placeholderText="mm/dd/yyyy"
                minDate={new Date()}
                maxDate={new Date(2099, 11, 31)}
                required
                customInput={
                  <DatePickerFieldTrigger placeholderText="mm/dd/yyyy" />
                }
              />
            ) : (
              <Select
                id="endOption"
                label="End Date"
                labelLayout="inline"
                required
                placeholder="Select"
                options={END_OPTION_VALUES}
                value={endOption || ""}
                onChange={handleEndOptionChange}
                isClearable
              />
            )}
          </div>
          <div className="col-md-3"></div>
        </div>

        <div className="row mt-1">
          <div className="col-md-2"></div>
          <div className="col-md-7">
            <TextArea
              id="reason"
              name="reason"
              label="Reason"
              labelLayout="inline"
              required
              rows={4}
              value={formData.reason}
              onChange={handleInputChange}
              error={errors.reason}
            />
          </div>
          <div className="col-md-3"></div>
        </div>
      </div>

      <br />

      <div className="action text-center d-flex justify-content-center gap-2">
        <Button type="button" variant="light-grey" onClick={handleReset}>
          Reset
        </Button>
        <Button type="submit" variant="accent" disabled={isLoading}>
          {isLoading ? (
            <span className={styles.submittingDots}>
              <ThreeDots
                height="20"
                width="44"
                radius="9"
                color="white"
                ariaLabel="three-dots-loading"
                visible
              />
            </span>
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </form>
  );
};

export default AccessRequest;
